import {
  ACTIVITY_JOURNAL_MAX_BYTES, ACTIVITY_JOURNAL_PREFIX, ACTIVITY_MAX_PAST_DAYS, ACTIVITY_OUTBOX_MAX_ENTRIES,
  type OutboxEntry,
} from './activity.model'

/** Entries older than this (by `oldestT`) would be refused by the server (14 d, with a 1 h margin). */
export const ACTIVITY_EXPIRY_MS = ACTIVITY_MAX_PAST_DAYS * 24 * 3600_000 - 3600_000

export const isExpired = (e: OutboxEntry, wall: number): boolean => wall - e.oldestT > ACTIVITY_EXPIRY_MS

export interface ActivityOutboxStore {
  put(entry: OutboxEntry): Promise<void>
  delete(id: string): Promise<void>
  getAll(): Promise<OutboxEntry[]>
  clear(): Promise<void>
}

/** Drops the oldest entries beyond the cap (by `createdAtWall`). Pure. */
export function entriesOverCap(all: OutboxEntry[], max = ACTIVITY_OUTBOX_MAX_ENTRIES): string[] {
  if (all.length <= max) return []
  return [...all].sort((a, b) => a.createdAtWall - b.createdAtWall).slice(0, all.length - max).map(e => e.id)
}

export class MemoryOutboxStore implements ActivityOutboxStore {
  private readonly map = new Map<string, OutboxEntry>()
  async put(entry: OutboxEntry): Promise<void> { this.map.set(entry.id, entry) }
  async delete(id: string): Promise<void> { this.map.delete(id) }
  async getAll(): Promise<OutboxEntry[]> { return [...this.map.values()] }
  async clear(): Promise<void> { this.map.clear() }
}

const DB_NAME = 'rse-activity'
const STORE = 'outbox'

const req = <T>(r: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => { r.onsuccess = () => resolve(r.result); r.onerror = () => reject(r.error) })

/** IndexedDB outbox (raw API); falls back to memory for the page life if IndexedDB is unusable. */
export class IdbOutboxStore implements ActivityOutboxStore {
  private db: Promise<IDBDatabase> | null = null
  private fallback: MemoryOutboxStore | null = null

  private open(): Promise<IDBDatabase> {
    this.db ??= new Promise<IDBDatabase>((resolve, reject) => {
      const open = indexedDB.open(DB_NAME, 1)
      open.onupgradeneeded = () => { open.result.createObjectStore(STORE, { keyPath: 'id' }) }
      open.onsuccess = () => resolve(open.result)
      open.onerror = () => reject(open.error)
      open.onblocked = () => reject(new Error('indexedDB blocked'))
    })
    return this.db
  }

  private async run<T>(mode: IDBTransactionMode, f: (s: IDBObjectStore) => IDBRequest, mem: (m: MemoryOutboxStore) => Promise<T>): Promise<T> {
    if (this.fallback) return mem(this.fallback)
    try {
      const db = await this.open()
      return (await req(f(db.transaction(STORE, mode).objectStore(STORE)))) as T
    } catch (err) {
      console.warn('activity: IndexedDB outbox unavailable, using memory:', err)
      this.fallback = new MemoryOutboxStore()
      return mem(this.fallback)
    }
  }

  async put(entry: OutboxEntry): Promise<void> {
    await this.run('readwrite', s => s.put(entry), m => m.put(entry))
    // the cap is checked by a cheap count; the full scan only happens when it is actually exceeded
    const count = await this.run('readonly', s => s.count(), async m => (await m.getAll()).length)
    if (count > ACTIVITY_OUTBOX_MAX_ENTRIES) for (const id of entriesOverCap(await this.getAll())) await this.delete(id)
  }
  async delete(id: string): Promise<void> { await this.run('readwrite', s => s.delete(id), m => m.delete(id)) }
  async getAll(): Promise<OutboxEntry[]> { return this.run('readonly', s => s.getAll(), m => m.getAll()) }
  async clear(): Promise<void> { await this.run('readwrite', s => s.clear(), m => m.clear()) }
}

/**
 * The synchronous unload journal (localStorage): written only by the hidden/close
 * flush, migrated into the outbox on the next visible moment or next load. Every
 * storage call is in try/catch (private windows, quota).
 */
export class UnloadJournal {
  constructor(private readonly storage: () => Storage | null = () => {
    try { return globalThis.localStorage ?? null } catch { return null }
  }) {}

  private keys(): string[] {
    const s = this.storage()
    if (!s) return []
    const out: string[] = []
    try {
      for (let i = 0; i < s.length; i++) {
        const k = s.key(i)
        if (k?.startsWith(ACTIVITY_JOURNAL_PREFIX)) out.push(k)
      }
    } catch { /* storage unavailable */ }
    return out
  }

  private read(key: string): OutboxEntry[] {
    try {
      const raw = this.storage()?.getItem(key)
      const parsed: unknown = raw ? JSON.parse(raw) : []
      return Array.isArray(parsed) ? parsed as OutboxEntry[] : []
    } catch { return [] }
  }

  readAll(): OutboxEntry[] { return this.keys().flatMap(k => this.read(k)) }

  /** Appends an entry under its session's key, dropping the oldest journalled entries beyond 1 MB in total. */
  append(entry: OutboxEntry): void {
    const s = this.storage()
    if (!s) return
    try {
      const key = ACTIVITY_JOURNAL_PREFIX + entry.sessionId
      s.setItem(key, JSON.stringify([...this.read(key), entry]))
      this.enforceCap()
    } catch { /* quota: the entry is lost, accepted */ }
  }

  private enforceCap(): void {
    const s = this.storage()
    if (!s) return
    const size = () => this.keys().reduce((n, k) => n + (s.getItem(k)?.length ?? 0), 0)
    let guard = 1000
    while (size() > ACTIVITY_JOURNAL_MAX_BYTES && guard-- > 0) {
      const all = this.keys().flatMap(k => this.read(k).map(e => ({ k, e })))
      if (all.length === 0) return
      const oldest = all.reduce((a, b) => (a.e.createdAtWall <= b.e.createdAtWall ? a : b))
      const rest = this.read(oldest.k).filter(e => e.id !== oldest.e.id)
      if (rest.length) s.setItem(oldest.k, JSON.stringify(rest)); else s.removeItem(oldest.k)
    }
  }

  /** Removes one entry (e.g. its keepalive send returned 200). */
  remove(entry: Pick<OutboxEntry, 'id' | 'sessionId'>): void {
    const s = this.storage()
    if (!s) return
    try {
      const key = ACTIVITY_JOURNAL_PREFIX + entry.sessionId
      const rest = this.read(key).filter(e => e.id !== entry.id)
      if (rest.length) s.setItem(key, JSON.stringify(rest)); else s.removeItem(key)
    } catch { /* ignore */ }
  }

  /**
   * Moves everything into the outbox (same `id`, so repeating is idempotent), then forgets it.
   * `shouldAbort` is checked before every write and after each put: once true the purge owns the
   * cleanup, so nothing may be (or stay) recreated in the store.
   */
  async migrateInto(store: ActivityOutboxStore, shouldAbort: () => boolean = () => false): Promise<void> {
    for (const key of this.keys()) {
      for (const e of this.read(key)) {
        if (shouldAbort()) return
        await store.put(e)
        // a put already in flight when the purge ran lands after it: take that row back out
        if (shouldAbort()) { await store.delete(e.id).catch(() => undefined); return }
      }
      if (shouldAbort()) return
      try { this.storage()?.removeItem(key) } catch { /* ignore */ }
    }
  }

  purgeAll(): void {
    const s = this.storage()
    for (const k of this.keys()) { try { s?.removeItem(k) } catch { /* ignore */ } }
  }
}
